// src/components/tools/room-ideas/RoomIdeaTool.jsx

import React, { useState } from "react";
import RoomIdeaForm from "./RoomIdeaForm";
import RoomIdeaResults from "./RoomIdeaResults";
import {
  DEFAULT_FORM_VALUES
} from "./constants";
import {
  validateRoomIdeaForm,
  canSubmitRoomIdeaForm
} from "./validators";

const RoomIdeaTool = () => {
  const [formValues, setFormValues] = useState({
    ...DEFAULT_FORM_VALUES,
    referenceImage: null // ✅ base64 image (optional)
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [apiError, setApiError] = useState(null);

  // -----------------------------
  // Form Updates
  // -----------------------------
  const updateField = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear field-level error on change
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  // -----------------------------
  // Submit Handler
  // -----------------------------
  const handleSubmit = async () => {
    const validationErrors = validateRoomIdeaForm(formValues);
    setErrors(validationErrors);

    if (!canSubmitRoomIdeaForm(formValues)) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const res = await fetch(`https://api.home-helper.com/api/ai/room-ideas`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          roomType: formValues.roomType,
          style: formValues.style,
          budget: formValues.budget,
          primaryColor: formValues.primaryColor,
          secondaryColor: formValues.secondaryColor,
          referenceImage: formValues.referenceImage || null
        })
      });

      if (!res.ok) {
        throw new Error("AI generation failed");
      }

      const data = await res.json();

      setResults({
        images: data.images,
        request: {
          roomType: formValues.roomType,
          style: formValues.style,
          budget: formValues.budget,
          primaryColor: formValues.primaryColor,
          secondaryColor: formValues.secondaryColor
        }
      });
    } catch (err) {
      console.error("Room idea generation failed:", err);
      setApiError(
        "Something went wrong generating ideas. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // -----------------------------
  // Reset Flow
  // -----------------------------
  const handleReset = () => {
    setFormValues({
      ...DEFAULT_FORM_VALUES,
      referenceImage: null
    });
    setErrors({});
    setResults(null);
    setApiError(null);
    setIsSubmitting(false);
  };

  // -----------------------------
  // Render
  // -----------------------------
  if (results) {
    return (
      <RoomIdeaResults
        results={results}
        onReset={handleReset}
      />
    );
  }

  return (
    <RoomIdeaForm
      values={formValues}
      errors={errors}
      isSubmitting={isSubmitting}
      apiError={apiError}
      onChange={updateField}
      onSubmit={handleSubmit}
    />
  );
};

export default RoomIdeaTool;
